/**
 * Open Orders visibility section (CR-4422).
 * Each toggle, when ON, hides the corresponding control in the Open Orders view.
 */

import { useState, useEffect } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../../ui/card';
import { Label } from '../../ui/label';
import { Switch } from '../../ui/switch';
import { Loader2, EyeOff } from 'lucide-react';
import { platformSettingsAPI } from '../../../lib/api';
import type { OpenOrdersVisibility } from '../../../lib/api';
import { toast } from 'sonner';

type VisibilityKey =
  | 'hide_exceptions'
  | 'hide_decline_button'
  | 'hide_propose_qty_ordered'
  | 'hide_actions'
  | 'hide_trend_line'
  | 'hide_otif_override_button'
  | 'hide_forecasting_from_customer'
  | 'hide_communication_box';

interface ToggleDef {
  key: VisibilityKey;
  label: string;
  description: string;
}

const TOGGLE_DEFS: ToggleDef[] = [
  {
    key: 'hide_exceptions',
    label: 'Hide Exceptions',
    description: 'Hide the Exceptions indicator on Open Orders rows.',
  },
  {
    key: 'hide_actions',
    label: 'Hide Actions',
    description: 'Hide the row Actions column on Open Orders.',
  },
  {
    key: 'hide_decline_button',
    label: 'Hide Decline Button',
    description: 'Hide the Decline button on PO lines in Open Orders.',
  },
  {
    key: 'hide_trend_line',
    label: 'Hide Trend Line',
    description: 'Hide the delivery trend line in Open Orders.',
  },
  {
    key: 'hide_communication_box',
    label: 'Hide Communication Box',
    description: 'Hide the Communication tab on Sales Order (PO) lines across the portal.',
  },
  {
    key: 'hide_otif_override_button',
    label: 'Hide OTIF Override Button',
    description: 'Hide the OTIF override control in Open Orders.',
  },
  {
    key: 'hide_propose_qty_ordered',
    label: 'Hide Propose Qty Ordered',
    description: 'Hide the Propose Qty Ordered control in Open Orders.',
  },
  {
    key: 'hide_forecasting_from_customer',
    label: 'Hide Forecasting from Customer',
    description: 'Hide forecasting details from the customer view of Open Orders.',
  },
];

const DEFAULT_VISIBILITY: OpenOrdersVisibility = {
  hide_exceptions: false,
  hide_decline_button: false,
  hide_propose_qty_ordered: false,
  hide_actions: false,
  hide_trend_line: false,
  hide_otif_override_button: false,
  hide_forecasting_from_customer: false,
  hide_communication_box: false,
};

export function OpenOrdersSection() {
  const [loading, setLoading] = useState(true);
  const [visibility, setVisibility] = useState<OpenOrdersVisibility>(DEFAULT_VISIBILITY);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await platformSettingsAPI.getSettings();
        const stored = (data.open_orders_visibility ?? {}) as Partial<OpenOrdersVisibility>;
        const merged: OpenOrdersVisibility = { ...DEFAULT_VISIBILITY };
        (Object.keys(DEFAULT_VISIBILITY) as VisibilityKey[]).forEach((k) => {
          const v = stored[k];
          if (typeof v === 'boolean') merged[k] = v;
        });
        setVisibility(merged);
      } catch (err: unknown) {
        console.error('Error loading open orders visibility:', err);
        toast.error('Failed to load Open Orders visibility settings');
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  const handleToggle = async (key: VisibilityKey, checked: boolean) => {
    const previous = visibility;
    const next: OpenOrdersVisibility = { ...previous, [key]: checked };
    setVisibility(next);
    try {
      await platformSettingsAPI.patchSettings({ open_orders_visibility: next });
      toast.success(`${checked ? 'Hidden' : 'Visible'}: ${labelFor(key)}`);
    } catch (err: unknown) {
      console.error('Error saving open orders visibility:', err);
      toast.error('Failed to save Open Orders visibility');
      setVisibility(previous);
    }
  };

  if (loading) {
    return (
      <Card>
        <CardContent className="flex items-center justify-center py-10">
          <Loader2 className="h-6 w-6 animate-spin text-muted-foreground" />
        </CardContent>
      </Card>
    );
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle className="flex items-center gap-2">
          <EyeOff className="h-5 w-5" />
          Open Orders
        </CardTitle>
        <p className="text-sm text-muted-foreground mt-1">
          Turn a toggle ON to hide the corresponding control in the Open Orders view.
        </p>
      </CardHeader>
      <CardContent>
        <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
          {TOGGLE_DEFS.map((t) => (
            <div
              key={t.key}
              className="flex items-center justify-between rounded-md border border-input bg-background px-4 py-3"
            >
              <div className="space-y-1 pr-3">
                <Label htmlFor={`open-orders-${t.key}`} className="text-sm font-medium">
                  {t.label}
                </Label>
                <p className="text-xs text-muted-foreground">{t.description}</p>
              </div>
              <Switch
                id={`open-orders-${t.key}`}
                checked={!!visibility[t.key]}
                onCheckedChange={(checked) => handleToggle(t.key, checked)}
              />
            </div>
          ))}
        </div>
      </CardContent>
    </Card>
  );
}

function labelFor(key: VisibilityKey): string {
  const def = TOGGLE_DEFS.find((t) => t.key === key);
  return def ? def.label : key;
}
